/**
 * Tokens for the Dashboard's seasonal promotions — the strip under the page
 * header and the popup. Source: `Dashboard - Promotions.dc.html`.
 *
 * Deliberate campaign-only exception to the brand-red rule (CLAUDE.md §11.5):
 * the peach accents on the Black Friday surfaces and the Year-End
 * orange→pink strip gradient are kept as designed, by product decision, and
 * live only here. Nothing outside the promo should read these values; every
 * real button fill still comes from the global button theme.
 *
 * Near-duplicates of existing tokens were folded onto them: the mock's
 * #21143D/#24153F inks → `signer.ink`, #5E5770 body → the modal body colour,
 * #7A7390 meta → `signer.text.meta` (3.9:1 → 5.5:1 on white, WCAG 1.4.3),
 * #E7E4EE/#E7E2F0 hairlines → `signer.border.main`.
 */
import appModalTokens from "global/components/AppModal/AppModal.tokens";
import { signer, white } from "theme/colors";
import { PromoCampaign } from "helpers/promotionHelpers";
import { statAccents } from "./DashboardReferenceStyles";

const peach = "#FFAD80";

export interface PromoTheme {
  /** Strip surface. */
  bg: string;
  fg: string;
  /** Emphasised runs inside the strip line. */
  boldText: string;
  /** Glyph colour inside the icon tile. */
  accent: string;
  tileBg: string;
  badgeBg: string;
  badgeFg: string;
  ctaBg: string;
  ctaFg: string;
  glow: string;
  decoStroke: string;
  decoOpacity: number;
  /** Popup: left hero panel and its big figures. */
  heroBg: string;
  heroAccent: string;
  /** Popup: "70% off" chip on each offer row. */
  chipBg: string;
}

export const promoThemes: Record<PromoCampaign, PromoTheme> = {
  bf: {
    bg: signer.ink,
    fg: white,
    boldText: peach,
    accent: peach,
    tileBg: "rgba(255,255,255,0.1)",
    badgeBg: "rgba(255,173,128,0.16)",
    badgeFg: peach,
    ctaBg: white,
    ctaFg: signer.ink,
    glow: "rgba(33,20,61,0.28)",
    decoStroke: peach,
    decoOpacity: 0.22,
    heroBg: "linear-gradient(150deg, #1B1033 0%, #2D1955 60%, #432879 100%)",
    heroAccent: peach,
    chipBg: "#FFE9DC",
  },
  ye: {
    // Ink on the gradient: 6.1:1 at the orange stop, 5.0:1 at the pink one.
    bg: "linear-gradient(100deg, #F96A24 0%, #EF4868 100%)",
    fg: signer.ink,
    boldText: signer.ink,
    accent: signer.ink,
    tileBg: "rgba(33,21,71,0.12)",
    badgeBg: signer.ink,
    badgeFg: white,
    ctaBg: signer.ink,
    ctaFg: white,
    glow: "rgba(239,72,104,0.28)",
    decoStroke: signer.ink,
    decoOpacity: 0.14,
    heroBg: "linear-gradient(150deg, #24153F 0%, #3E1D55 60%, #6A2552 100%)",
    heroAccent: "#FF9166",
    chipBg: "#FFE6DA",
  },
};

export const promoTokens = {
  strip: {
    radius: "14px",
    padding: "12px 12px 12px 14px",
    gap: "14px",
    tileSize: 40,
    tileRadius: "11px",
    iconSize: 19,
    badgeFontSize: "0.59375rem", // 9.5
    badgeLetterSpacing: "0.12em",
    badgePadding: "3px 9px",
    textFontSize: "0.84375rem", // 13.5
    ctaHeight: 36,
    ctaRadius: "9px",
    ctaFontSize: "0.8125rem", // 13
    dismissSize: 32,
    dismissRadius: "8px",
    dismissGlyph: 14,
  },
  popup: {
    width: 880,
    radius: "22px",
    shadow: "0 30px 80px rgba(18,10,36,0.4)",
    heroBasis: "44%",
    heroPad: { xs: "22px 22px 20px", md: "40px 44px" },
    heroEyebrow: "#E4DDF2",
    heroRule: "rgba(255,255,255,0.18)",
    heroNum: { xs: "4rem", md: "7.5rem" }, // 64 / 120
    heroNum2: { xs: "2.125rem", md: "3.25rem" }, // 34 / 52
    heroLine: { xs: "0.875rem", md: "1rem" }, // 14 / 16
    heroRuleMargin: { xs: "14px 0", md: "24px 0" },
    // Decorative floating document cards on the hero.
    card: {
      bg: "rgba(255,255,255,0.1)",
      border: "1px solid rgba(255,255,255,0.14)",
      lineStrong: "rgba(255,255,255,0.28)",
      line: "rgba(255,255,255,0.16)",
      squiggle: "rgba(255,173,128,0.6)",
    },
    bodyPad: { xs: "22px 20px 18px", md: "40px 40px 28px" },
    titleSize: { xs: "1.5625rem", md: "2rem" }, // 25 / 32
    ink: signer.ink,
    body: appModalTokens.color.body,
    meta: signer.text.meta,
    secondary: signer.text.secondary,
    border: signer.border.main,
    closeHoverBg: signer.surface.muted,
    check: statAccents.completed.accent,
  },
};
